import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _isFunction from 'lodash/isFunction';
import _map from 'lodash/map';
import _head from 'lodash/head';

import { IPropsWithClassName } from 'interfaces';
import Heading from '@atoms/Heading';
import LoadingBox from '@atoms/LoadingBox';
import Button, { ButtonProps } from '@atoms/Button';
import ButtonType from '@atoms/Button/type';
import ToggleButtons from '@molecules/ToggleButtons';
import Pager from '@molecules/Pager';
import patternsIcons from '@organisms/LayerPattern/data';
import {
  LayerPatternType,
  UnitDimension,
  UnitPackaging,
} from 'enums/calculator';
import useLayoutConfigState from 'hooks/useLayoutConfigState';
import usePagination from 'hooks/usePagination';
import { LayerPatternItem } from 'interfaces/calculator';
import useCalculatorState from 'hooks/useCalculatorState';
import { CalculatorOptions, patternIsVisible } from 'utils/calculator';
import PatternId from 'enums/patterns';

import styles from './styles';

export interface LayerPatternsProps extends IPropsWithClassName {
  unitsPerLayer?: number | null;
  onListChange?(units: number | null): void;
}

const LayerPatterns = ({
  className,
  unitsPerLayer,
  onListChange,
}: LayerPatternsProps) => {
  const { t } = useTranslation();
  const { dimensions, palletData, packagingType } = useCalculatorState();
  const {
    length: packageLength,
    type: packageDimensionType,
    width: packageWidth,
  } = dimensions;
  const {
    length: palletLength,
    unitType: palletUnitType,
    width: palletWidth,
  } = palletData;

  const {
    items,
    activeItem,
    setActivePattern,
    setPatternType,
    type,
    horizontalOverlap,
    verticalOverlap,
  } = useLayoutConfigState();

  const filterItems = useCallback(
    (elements: any[]) =>
      _filter(elements, (pattern) => {
        const overlapAllowed = packagingType === UnitPackaging.OVERLAP_BAG;
        const options: CalculatorOptions = {
          unitsPerLayer,
          packageLength,
          packageDimensionType,
          packageWidth,
          palletLength,
          palletUnitType,
          palletWidth,
          alignmentType: type,
          horizontalOverlap,
          verticalOverlap,
        };
        return patternIsVisible(pattern, options, overlapAllowed);
      }),
    [
      unitsPerLayer,
      packageLength,
      packageDimensionType,
      packageWidth,
      palletLength,
      palletUnitType,
      palletWidth,
      type,
      horizontalOverlap,
      verticalOverlap,
      packagingType,
    ]
  );

  const [itemsToPaginate, setItemsToPaginate] = useState(() =>
    filterItems(items)
  );
  const { currentPage, gotToPage, pageElements, totalPages } =
    usePagination<LayerPatternItem>(itemsToPaginate, activeItem);

  /* Filter items and set them to paginate.
   *  Also, set the default pattern if there is no pattern selected
   */
  useEffect(() => {
    const itemsFiltered = filterItems(items);
    const activeExistsOnFiltered = _find(itemsFiltered, {
      label: activeItem?.label,
    });
    const { label } = activeItem || {};
    const isGridPattern = label === PatternId.GRID;
    if (activeItem && !activeExistsOnFiltered) {
      /*
       * Set active the first pattern
       * if the last selected pattern does not exist in the new filtered elements
       */
      const newActiveItem = _head(itemsFiltered);
      if (newActiveItem) {
        setActivePattern(newActiveItem.label);
      }
    } else if (isGridPattern) {
      setActivePattern(label);
    }

    setItemsToPaginate(itemsFiltered);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    unitsPerLayer,
    setItemsToPaginate,
    type,
    horizontalOverlap,
    verticalOverlap,
    packagingType,
  ]);

  const onPaginationChange = useCallback(() => {
    if (_isFunction(onListChange)) {
      onListChange(itemsToPaginate.length);
    }
  }, [itemsToPaginate, onListChange]);

  useEffect(() => {
    onPaginationChange();
  }, [itemsToPaginate, onPaginationChange]);

  const itemsWithIcons = _map(pageElements, (item) => {
    const itemData = _find(patternsIcons, { label: item.label });
    return {
      ...item,
      icon: itemData && itemData.icon,
      selected: (activeItem && item.label === activeItem.label) || false,
    };
  });
  const componentClasses = classNames(className);

  const typeOfPatterns: ButtonProps<LayerPatternType>[] = [
    {
      type: ButtonType.PILL,
      children: LayerPatternType.ALIGNED.toLowerCase(),
      active: type === LayerPatternType.ALIGNED,
      className: styles.toggleButton1,
      data: LayerPatternType.ALIGNED,
    },
    {
      type: ButtonType.PILL,
      children: LayerPatternType.OVERHANG.toLowerCase(),
      active: type === LayerPatternType.OVERHANG,
      className: styles.toggleButton2,
      data: LayerPatternType.OVERHANG,
    },
  ];

  const showPagination = itemsWithIcons && itemsWithIcons.length > 0;

  const onTypeOfPatternChange = (item: ButtonProps) =>
    setPatternType(item.data);
  const onPatternClick = (item: LayerPatternItem) =>
    setActivePattern(item.label);
  const onPageChange = (newPage: number) => gotToPage(newPage);

  return (
    <section className={componentClasses}>
      <div className="sm:flex justify-between items-center">
        <Heading className="mt-10">Layer Pattern</Heading>
        <ToggleButtons
          className={styles.unitToggle}
          items={typeOfPatterns}
          onChange={onTypeOfPatternChange}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {_map(itemsWithIcons, (item) => {
          const { label, icon: Icon, selected } = item;
          return (
            <Button
              key={label}
              active={selected}
              type={ButtonType.OUTLINE}
              className="text-center block w-full"
              onClick={() => onPatternClick(item)}
            >
              <React.Fragment>
                {Icon && <Icon className="mx-auto mb-1" />}
                <span className="text-sm text-darkGray font-normal">
                  <Trans>{label}</Trans>
                </span>
              </React.Fragment>
            </Button>
          );
        })}
      </div>
      {!showPagination && <LoadingBox>{t('No pattern')}</LoadingBox>}
      <div className="mt-4">
        {showPagination && (
          <Pager
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </section>
  );
};

export default LayerPatterns;
